import React, { useState, useEffect, useMemo, useCallback, useContext, createContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { schools as schoolsApi, exams as examsApi, mocks as mocksApi } from '../api';
import Navbar from '../components/Navbar';

/**
 * Teacher dashboard (W6-7, chunk D4).
 *
 * Route:  /school            -> school leader's own org (user.org_id)
 *         /school/:orgId     -> drfahm_admin viewing any org (backend still
 *                               enforces; a leader hitting another id gets 403)
 * Data:   GET /api/schools/:orgId/dashboard  (D2) — one response, no polling.
 *
 * Panels (locked design, Chunk 21 s5):
 *   1. Class overview      activity rings + predicted-score donut
 *   2. Needs attention     flags with reason + action, red -> amber
 *   3. Class topic profile radar (one per section) — full-width card (23.5)
 *   4. Mastery grid        student x topic, class row at bottom — full width
 *   5. Roster              sortable table
 *   6. School league       weekly EFFORT board (D3) — gold/silver/bronze +
 *                          class-vs-class totals; resets at week start
 * Charts are inline SVG on purpose: package.json has no chart library and
 * the radar/ring/donut are small enough that adding one is not justified.
 *
 * i18n: every string goes through t() with an English defaultValue. D5
 * added the real teacher.* keys to both locale files; the defaultValues stay
 * as a safety net so a missing key can never blank a label.
 *
 * CSV export (D5) is built client-side from the roster already in memory —
 * the backend has no export endpoint and does not need one for this.
 *
 * Branding (Chunk 23): data.meta.branding = { name, logo_url, primary_hex },
 * every key present, null = DrFahm default. When primary_hex is set the page
 * root gets class td-branded plus CSS variables (--td-brand and shades) so
 * the .td-* rules that read var(--brand-navy) / var(--brand-green) pick up
 * the school colour; SVG ink that used C.navy reads the same colour through
 * PaletteContext. Data ink (C.data, the green traffic-light) is deliberately
 * NOT rebranded: green must keep meaning "good" on every school's dashboard.
 * The logo renders in the page header and the print header. Navbar stays
 * DrFahm — the product frame is ours, the report inside it is theirs.
 *
 * Topic labels (23.6): the response carries label + label_ar on every topic
 * and on weakest_topic. On the AR page the roster, CSV and radar show
 * "Arabic (English)"; the mastery grid shows Arabic only (vertical headers
 * have no room) with both in the tooltip. label_ar equal to label means
 * "not translated yet" and renders as plain English.
 *
 * Radar (23.6): every topic of the section is an axis, always. Reached
 * topics get a dot + value; unreached ones a muted label and a dash — never
 * a zero, because 0 means "attempted, not passed". The polygon is drawn
 * through reached topics only once three or more have data.
 *
 * No curly apostrophes anywhere in this file (Vercel ESLint).
 */

const EXAMS = ['qudurat', 'tahsili'];

// ── Colour helpers (brand tokens, see global.css :root) ──────────────────────

const C = {
  green:    '#0B5D4B',   // brand green — text/borders only
  data:     '#16A34A',   // bright green — chart ink (rings, donut, radar, grid)
  cta:      '#15803D',
  navy:     '#1C2733',
  gold:     '#C6A85B',
  sand:     '#F5F2EC',
  grey:     '#D6DBE0',
  red:      '#B91C1C',
  muted:    '#8A909A',
  card2:    '#EDE9E1',
};

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

// ── Per-org branding (Chunk 23) ──────────────────────────────────────────────

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

// t in (0, 1]: darken toward black. Used for hover/pressed states.
function darken(hex, t) {
  const [r, g, b] = hexToRgb(hex);
  return `rgb(${Math.round(r * (1 - t))}, ${Math.round(g * (1 - t))}, ${Math.round(b * (1 - t))})`;
}

function alpha(hex, a) {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

// Everything the page needs from meta.branding, with fallbacks applied once.
function resolveBranding(meta) {
  const b = meta?.branding || {};
  const hex = typeof b.primary_hex === 'string' && HEX_RE.test(b.primary_hex) ? b.primary_hex.toUpperCase() : null;
  return {
    name: (b.name && String(b.name).trim()) || meta?.org?.name || null,
    logo: (b.logo_url && String(b.logo_url).trim()) || null,
    hex,
  };
}

// Inline CSS variables for the page root. The .td-branded rules in global.css
// map these onto --brand-navy / --brand-green for the dashboard subtree only.
function brandStyle(hex) {
  if (!hex) return undefined;
  return {
    '--td-brand':       hex,
    '--td-brand-hover': darken(hex, 0.22),
    '--td-brand-soft':  alpha(hex, 0.10),
    '--td-brand-focus': alpha(hex, 0.45),
  };
}

// SVG components read C.navy for text/ticks; a branded org swaps that one
// token so inline SVG matches the CSS-driven headings.
const PaletteContext = createContext(C);
function usePalette() { return useContext(PaletteContext); }

function mix(a, b, t) {
  const [r1, g1, b1] = hexToRgb(a);
  const [r2, g2, b2] = hexToRgb(b);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const bl = Math.round(b1 + (b2 - b1) * t);
  return `rgb(${r}, ${g}, ${bl})`;
}

// Mastery 0..100 -> red (0) -> gold (50) -> green (100). null -> neutral.
function masteryColor(v) {
  if (v == null) return C.card2;
  const x = Math.max(0, Math.min(100, v));
  return x < 50 ? mix(C.red, C.gold, x / 50) : mix(C.gold, C.data, (x - 50) / 50);
}

function masteryText(v) {
  if (v == null) return C.muted;
  return v >= 35 && v <= 70 ? C.navy : '#FFFFFF';
}

function fmtDate(iso, locale) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  } catch {
    return iso.slice(0, 10);
  }
}

// Topic display label. AR: "Arabic (English)" unless the Arabic is missing
// (backend falls back label_ar = label), then plain English.
function topicLabel(t, isAr) {
  if (!t) return '';
  if (isAr && t.label_ar && t.label_ar !== t.label) return `${t.label_ar} (${t.label})`;
  return t.label;
}

// Compact variant for the grid's vertical headers: one language only.
function topicLabelShort(t, isAr) {
  if (!t) return '';
  return isAr && t.label_ar ? t.label_ar : t.label;
}

// Count-up for hero numbers on first render; snaps to the final value on
// re-render and respects prefers-reduced-motion.
function useCountUp(target, duration = 600) {
  const [value, setValue] = useState(target == null ? null : 0);
  useEffect(() => {
    if (target == null) { setValue(null); return undefined; }
    const reduce = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setValue(target); return undefined; }
    let raf = 0;
    const start = performance.now();
    const from = 0;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(from + (target - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

// Participation is the one ring where low is bad: traffic-light it.
function participationColor(pct) {
  if (pct == null) return C.grey;
  if (pct >= 70) return C.data;
  if (pct >= 40) return C.gold;
  return C.red;
}

// ── CSV export (D5) ──────────────────────────────────────────────────────────

function csvCell(v) {
  if (v == null) return '';
  const str = String(v);
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

function buildRosterCsv(data, tx, isAr) {
  const tracks = data.overview.tracks;
  const header = [
    tx('csv.username', 'Username'),
    tx('csv.class', 'Class'),
    ...tracks.map((tr) => `${tr.track_name} ${tx('csv.passed', 'passed')}`),
    ...tracks.map((tr) => `${tr.track_name} %`),
    tx('csv.total_passed', 'Levels passed'),
    tx('csv.this_week', 'Passed this week'),
    tx('csv.predicted', 'Predicted score'),
    tx('csv.weakest', 'Weakest topic'),
    tx('csv.weakest_score', 'Weakest topic mastery'),
    tx('csv.last_active', 'Last active'),
    tx('csv.flags', 'Flags'),
  ];
  const rows = data.roster.map((r) => [
    r.username,
    r.class_group || '',
    ...tracks.map((tr) => `${r.tracks[tr.track_key].passed}/${r.tracks[tr.track_key].total}`),
    ...tracks.map((tr) => r.tracks[tr.track_key].pct),
    r.levels_passed_total,
    r.passed_this_week,
    r.predicted_score == null ? '' : r.predicted_score,
    r.weakest_topic ? topicLabel(r.weakest_topic, isAr) : '',
    r.weakest_topic ? Math.round(r.weakest_topic.mastery) : '',
    r.last_active_at ? r.last_active_at.slice(0, 10) : '',
    r.flag_types.map((f) => tx(`flags.type.${f}`, f.replace('_', ' '))).join('; '),
  ]);
  const lines = [header, ...rows].map((cols) => cols.map(csvCell).join(','));
  // BOM so Excel opens Arabic labels correctly.
  return '\uFEFF' + lines.join('\r\n');
}

function downloadCsv(filename, text) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── SVG: Ring ────────────────────────────────────────────────────────────────

function Ring({ value, label, sub, color = C.data, size = 132, stroke = 12, target = null, targetLabel = '' }) {
  const P = usePalette();
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value == null ? 0 : value));
  const shown = useCountUp(value == null ? null : pct);
  const dash = (pct / 100) * circ;
  const tick = target == null ? null : (() => {
    const a = -Math.PI / 2 + (2 * Math.PI * target) / 100;
    const ro = r + stroke / 2 + 2;
    const ri = r - stroke / 2 - 2;
    return { x1: size / 2 + ri * Math.cos(a), y1: size / 2 + ri * Math.sin(a),
             x2: size / 2 + ro * Math.cos(a), y2: size / 2 + ro * Math.sin(a) };
  })();
  return (
    <div className="td-ring">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${label}: ${Math.round(pct)}%`}>
        <style>{'.td-ring-fg { transition: stroke-dasharray 0.6s ease; }'}</style>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.grey} strokeWidth={stroke} />
        <circle
          className="td-ring-fg"
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${dash} ${circ - dash}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        {tick ? <line x1={tick.x1} y1={tick.y1} x2={tick.x2} y2={tick.y2} stroke={P.navy} strokeWidth="2.5" strokeLinecap="round" /> : null}
        <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle"
              fontFamily="Tajawal, sans-serif" fontWeight="800" fontSize={size * 0.22} fill={color === C.red ? C.red : P.navy}>
          {value == null ? '–' : `${Math.round(shown == null ? pct : shown)}%`}
        </text>
      </svg>
      {tick ? <div className="td-ring-target">{targetLabel}</div> : null}
      <div className="td-ring-label">{label}</div>
      {sub ? <div className="td-ring-sub">{sub}</div> : null}
    </div>
  );
}

// ── SVG: Donut with hero number ──────────────────────────────────────────────

function Donut({ score, caption, sub }) {
  const P = usePalette();
  const size = 184;
  const stroke = 16;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = score == null ? 0 : Math.max(0, Math.min(100, score));
  const shown = useCountUp(score == null ? null : score);
  const dash = (pct / 100) * circ;
  return (
    <div className="td-donut">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={caption}>
        <style>{'.td-donut-fg { transition: stroke-dasharray 0.6s ease; }'}</style>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.grey} strokeWidth={stroke} />
        <circle
          className="td-donut-fg"
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={C.data} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${dash} ${circ - dash}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text x="50%" y="46%" dominantBaseline="central" textAnchor="middle"
              fontFamily="Tajawal, sans-serif" fontWeight="800" fontSize="48" fill={P.navy}>
          {score == null ? '–' : Math.round(shown == null ? score : shown)}
        </text>
        <text x="50%" y="68%" dominantBaseline="central" textAnchor="middle"
              fontFamily="Tajawal, sans-serif" fontWeight="600" fontSize="11" fill={C.muted}>
          {score == null ? '' : '/ 100'}
        </text>
      </svg>
      <div className="td-ring-label">{caption}</div>
      {sub ? <div className="td-ring-sub">{sub}</div> : null}
    </div>
  );
}

// ── SVG: Radar ───────────────────────────────────────────────────────────────

function splitLabel(label, max = 22) {
  // Two lines for long topic names so they never leave the viewBox.
  if (label.length <= max) return [label];
  const words = label.split(' ');
  let best = null;
  for (let i = 1; i < words.length; i++) {
    const a = words.slice(0, i).join(' ');
    const b = words.slice(i).join(' ');
    const score = Math.abs(a.length - b.length) + (words[i] === '&' ? 100 : 0);
    if (best === null || score < best.score) best = { a, b, score };
  }
  return best ? [best.a, best.b] : [label];
}

function Radar({ axes }) {
  const P = usePalette();
  // axes: [{ lines: string[], value: 0..100 | null }] — every topic of the
  // section, in taxonomy order. value null = not reached by the class yet.
  // 23.5/23.6: full-width card. Radius 170 in an 860x470 box. Labels at
  // 1.13R with 13px text, up to three lines (AR: Arabic split at 22 chars
  // + "(English)" on its own line). Checked numerically against the real
  // taxonomy labels for every section, EN and AR: nothing leaves the box.
  const W = 860;
  const H = 470;
  const cx = W / 2;
  const cy = H / 2 + 4;
  const R = 170;
  const n = axes.length;
  const angle = (i) => -Math.PI / 2 + (2 * Math.PI * i) / n;
  const pt = (i, frac) => [cx + R * frac * Math.cos(angle(i)), cy + R * frac * Math.sin(angle(i))];
  const ringPath = (frac) => axes.map((_, i) => pt(i, frac).join(',')).join(' ');
  const reached = axes.map((a, i) => (a.value == null ? null : i)).filter((i) => i !== null);
  const drawShape = reached.length >= 3;
  const dataPath = reached.map((i) => pt(i, Math.max(0, Math.min(100, axes[i].value)) / 100).join(',')).join(' ');

  // direction="ltr" is load-bearing: under dir="rtl" the browser flips SVG
  // text-anchor (start = right edge), so right-side labels drew over the
  // chart and the & line-break reordered. Anchors here are geometric, not
  // reading-order. Arabic label text still shapes correctly inside an LTR box.
  return (
    <svg className="td-radar" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Class topic profile"
         direction="ltr" style={{ direction: 'ltr' }}>
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <polygon key={f} points={ringPath(f)} fill={f === 1 ? 'rgba(22,163,74,0.04)' : 'none'}
                 stroke={C.grey} strokeWidth={f === 1 ? 1.2 : 0.8} />
      ))}
      {axes.map((_, i) => {
        const [x, y] = pt(i, 1);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke={C.grey} strokeWidth="0.8" />;
      })}
      {drawShape ? (
        <polygon className="td-radar-area" points={dataPath}
                 fill="rgba(22,163,74,0.22)" stroke={C.data} strokeWidth="3" strokeLinejoin="round" />
      ) : null}
      {axes.map((a, i) => {
        if (a.value == null) return null;
        const [x, y] = pt(i, Math.max(0, Math.min(100, a.value)) / 100);
        return <circle key={i} cx={x} cy={y} r="5" fill={C.data} stroke="#fff" strokeWidth="1.5" />;
      })}
      {axes.map((a, i) => {
        const [x, y] = pt(i, 1.13);
        const dx = x - cx;
        const anchor = Math.abs(dx) < 14 ? 'middle' : dx > 0 ? 'start' : 'end';
        const lines = a.lines;
        const above = y < cy - 4;
        // Above the centre the block grows upward so its last line sits at y;
        // below/beside it grows downward from y.
        const firstDy = above ? -15 * (lines.length - 1) : 0;
        const reachedAxis = a.value != null;
        return (
          <text key={i} x={x} y={y} textAnchor={anchor} dominantBaseline="central"
                fontFamily="Tajawal, sans-serif" fontSize="13" fontWeight={reachedAxis ? '600' : '500'}
                fill={reachedAxis ? P.navy : C.muted}>
            {lines.map((ln, li) => (
              <tspan key={li} x={x} dy={li === 0 ? firstDy : 15}>
                {ln}
                {li === lines.length - 1
                  ? <tspan fill={C.muted} fontWeight="400"> {reachedAxis ? Math.round(a.value) : '\u2013'}</tspan>
                  : null}
              </tspan>
            ))}
          </text>
        );
      })}
    </svg>
  );
}

// ── Panels ───────────────────────────────────────────────────────────────────

function ContextStrip({ data, tx, i18n }) {
  const ws = new Date(data.meta.week_start);
  const we = new Date(ws.getTime() + 6 * 86400000);
  const fmt = (d) => d.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
  const when = (() => {
    try { return new Date(data.meta.generated_at).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  })();
  const nClasses = data.meta.classes.length;
  const parts = [
    tx('context.week', 'Week of {{from}} to {{to}}', { from: fmt(ws), to: fmt(we) }),
    tx('context.students', '{{n}} students', { n: data.overview.students_total }),
    data.meta.class_group
      ? tx('context.class', 'class {{c}}', { c: data.meta.class_group })
      : (nClasses > 0 ? tx('context.classes', '{{n}} classes', { n: nClasses }) : null),
    tx('context.asof', 'data as of {{when}}', { when }),
  ].filter(Boolean);
  return <div className="td-context">{parts.join(' \u00b7 ')}</div>;
}

function StatTile({ n, label, tone }) {
  const shown = useCountUp(n);
  return (
    <div className={`td-stat td-stat-${tone}`}>
      <span className="td-stat-n">{Math.round(shown == null ? n : shown)}</span>
      <span className="td-stat-l">{label}</span>
    </div>
  );
}

function OverviewPanel({ data, tx, examName, isAr }) {
  const ov = data.overview;
  const total = ov.students_total || 0;
  const activePct = total ? Math.round((100 * ov.active_this_week) / total) : null;
  const conf = ov.predicted.confidence;
  return (
    <div className="card td-overview">
      <div className="td-overview-rings">
        <Ring
          value={activePct}
          label={tx('overview.active', 'Students who practised this week')}
          sub={tx('overview.active_sub', '{{n}} of {{total}} students logged in and attempted a level', { n: ov.active_this_week, total })}
          color={participationColor(activePct)}
          target={80}
          targetLabel={tx('overview.target', 'target 80%')}
        />
        {ov.tracks.map((tr) => (
          <Ring
            key={tr.track_key}
            value={tr.class_mean_pct}
            label={tx('overview.track', '{{track}} content completed', { track: isAr ? tr.track_name_ar : tr.track_name })}
            sub={tx('overview.track_sub', 'class average across {{total}} levels; {{n}} of {{students}} students have started', { total: tr.total_levels, n: tr.students_started, students: total })}
          />
        ))}
      </div>
      <div className="td-overview-donut">
        <Donut
          score={ov.predicted.score}
          caption={tx('overview.predicted', 'Predicted {{exam}} score', { exam: examName })}
          sub={
            ov.predicted.score == null
              ? tx('overview.predicted_none', 'No passed levels yet')
              : tx('overview.predicted_sub', 'class average of {{n}} students; {{conf}} confidence',
                   { n: ov.predicted.students_with_score, conf: tx(`confidence.${conf}`, conf) })
          }
        />
      </div>
      <div className="td-overview-stats">
        <StatTile n={ov.levels_passed_this_week} label={tx('overview.week_levels', 'levels passed this week')} tone="green" />
        <StatTile n={ov.levels_passed_total} label={tx('overview.total_levels', 'levels passed in total')} tone="navy" />
        <StatTile n={ov.never_started} label={tx('overview.never_started', 'not started')} tone={ov.never_started > 0 ? 'red' : 'muted'} />
      </div>
    </div>
  );
}

function FlagsPanel({ data, tx, onPick }) {
  const th = data.thresholds;
  const flags = data.flags;
  return (
    <div className="card td-flags">
      {flags.length === 0 ? (
        <div className="td-flags-empty">
          {tx('flags.none', 'No students need attention right now.')}
        </div>
      ) : (
        <ul className="td-flag-list">
          {flags.map((e) => (
            <li key={e.user_id} className={`td-flag td-flag-${e.severity}`}>
              <button type="button" className="td-flag-name" onClick={() => onPick(e.user_id)}>
                {e.username}
                {e.class_group ? <span className="td-flag-class">{e.class_group}</span> : null}
              </button>
              <div className="td-flag-body">
                {e.flags.map((f) => (
                  <div key={f.type} className="td-flag-row">
                    <span className={`td-flag-chip td-flag-chip-${f.severity}`}>{tx(`flags.type.${f.type}`, f.type.replace('_', ' '))}</span>
                    <span className="td-flag-reason">{f.reason}</span>
                    <span className="td-flag-action">{f.action}</span>
                  </div>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="td-rules">
        {tx('flags.rules',
            'Inactive: no attempt for {{inactive}} days. Stuck: {{stuck}} or more failed attempts on one level. Falling behind: {{behind}}% or more below the class median (applied when the class has at least {{min}} students).',
            { inactive: th.inactive_days, stuck: th.stuck_attempts, behind: th.behind_pct, min: th.behind_min_students })}
        {th.behind_applied ? null : ` ${tx('flags.behind_off', 'Falling-behind rule is not applied for this class yet.')}`}
      </div>
    </div>
  );
}

function RadarPanel({ data, tx, i18n }) {
  const sections = data.topics.sections;
  const [active, setActive] = useState(0);
  useEffect(() => { setActive(0); }, [data.meta.exam]);
  const sec = sections[Math.min(active, sections.length - 1)];
  const isAr = i18n.language === 'ar';
  const axes = (sec?.topics || []).map((t) => ({
    value: t.class_mean,
    lines: isAr && t.label_ar && t.label_ar !== t.label
      ? [...splitLabel(t.label_ar), `(${t.label})`]
      : splitLabel(t.label),
  }));
  const reachedCount = axes.filter((a) => a.value != null).length;
  return (
    <div className="card td-radar-card">
      {sections.length > 1 ? (
        <div className="td-tabs">
          {sections.map((s, i) => (
            <button key={s.section} type="button"
                    className={`td-tab${i === active ? ' td-tab-active' : ''}`}
                    onClick={() => setActive(i)}>
              {isAr ? s.section_name_ar : s.section_name}
            </button>
          ))}
        </div>
      ) : null}
      {axes.length >= 3 ? (
        <Radar axes={axes} />
      ) : (
        <div className="td-flags-empty">
          {tx('radar.not_enough', 'The class needs to reach at least three topics before a profile can be drawn.')}
        </div>
      )}
      {axes.length >= 3 && reachedCount < 3 ? (
        <div className="td-radar-note">
          {tx('radar.not_enough', 'The class needs to reach at least three topics before a profile can be drawn.')}
        </div>
      ) : null}
      <div className="td-rules">
        {tx('radar.legend', 'Each point is the class average for that topic: 100 means every level containing it was passed first time, 50 means passed after retries, 0 means still not passed.')}
      </div>
    </div>
  );
}

function GridPanel({ data, tx, highlight, isAr }) {
  const sections = data.topics.sections;
  const topics = sections.flatMap((s) => s.topics).filter((t) => t.students_with_data > 0);
  const students = data.matrix.students;
  if (students.length === 0 || topics.length === 0) {
    return (
      <div className="card td-flags-empty">
        {tx('grid.empty', 'The grid fills in as students pass levels.')}
      </div>
    );
  }
  return (
    <div className="card td-grid-card">
      <div className="td-grid-scroll">
        <table className="td-grid">
          <thead>
            <tr>
              <th className="td-grid-corner" />
              {topics.map((t) => <th key={t.topic} className="td-grid-th" title={topicLabel(t, isAr)}><span>{topicLabelShort(t, isAr)}</span></th>)}
            </tr>
          </thead>
          <tbody>
            {students.map((s) => (
              <tr key={s.user_id} id={`td-student-${s.user_id}`} className={highlight === s.user_id ? 'td-grid-hl' : ''}>
                <th className="td-grid-rowhead">{s.username}</th>
                {topics.map((t) => {
                  const v = s.mastery[t.topic];
                  return (
                    <td key={t.topic} className="td-grid-cell"
                        style={{ background: masteryColor(v), color: masteryText(v) }}
                        title={`${s.username} / ${topicLabel(t, isAr)}: ${v == null ? '-' : Math.round(v)}`}>
                      {v == null ? '' : Math.round(v)}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="td-grid-classrow">
              <th className="td-grid-rowhead">{tx('grid.class_row', 'Class')}</th>
              {topics.map((t) => {
                const v = data.matrix.class_row[t.topic];
                return (
                  <td key={t.topic} className="td-grid-cell"
                      style={{ background: masteryColor(v), color: masteryText(v) }}>
                    {v == null ? '' : Math.round(v)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
      <div className="td-rules">
        {tx('grid.legend', 'A red column means the whole class needs that topic taught again. A red row means one student needs time with you.')}
      </div>
    </div>
  );
}

function RosterPanel({ data, tx, i18n, highlight }) {
  const [sortKey, setSortKey] = useState('username');
  const [sortDir, setSortDir] = useState('asc');
  const tracks = data.overview.tracks;

  const rows = useMemo(() => {
    const val = (r) => {
      if (sortKey.startsWith('track:')) return r.tracks[sortKey.slice(6)]?.passed ?? 0;
      switch (sortKey) {
        case 'weakest':    return r.weakest_topic ? r.weakest_topic.mastery : 101;
        case 'last_active': return r.last_active_at || '';
        case 'flags':      return r.flag_types.length;
        default:           return r[sortKey] ?? '';
      }
    };
    const list = [...data.roster];
    list.sort((a, b) => {
      const va = val(a); const vb = val(b);
      const c = typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? c : -c;
    });
    return list;
  }, [data.roster, sortKey, sortDir]);

  const sortBy = (key) => {
    if (key === sortKey) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'username' ? 'asc' : 'desc'); }
  };
  const Th = ({ k, children }) => (
    <th>
      <button type="button" className={`td-sort${sortKey === k ? ' td-sort-active' : ''}`} onClick={() => sortBy(k)}>
        {children}{sortKey === k ? (sortDir === 'asc' ? ' \u2191' : ' \u2193') : ''}
      </button>
    </th>
  );

  if (data.roster.length === 0) {
    return (
      <div className="card td-flags-empty">
        {tx('roster.empty', 'No students in this class yet. Ask DrFahm to add accounts.')}
      </div>
    );
  }

  return (
    <div className="card td-roster-card">
      <div className="td-grid-scroll">
        <table className="td-roster">
          <thead>
            <tr>
              <Th k="username">{tx('roster.student', 'Student')}</Th>
              <Th k="class_group">{tx('roster.class', 'Class')}</Th>
              {tracks.map((tr) => <Th key={tr.track_key} k={`track:${tr.track_key}`}>{tr.track_name}</Th>)}
              <Th k="weakest">{tx('roster.weakest', 'Weakest topic')}</Th>
              <Th k="passed_this_week">{tx('roster.week', 'This week')}</Th>
              <Th k="last_active">{tx('roster.last_active', 'Last active')}</Th>
              <Th k="flags">{tx('roster.flags', 'Flags')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id} className={highlight === r.user_id ? 'td-grid-hl' : ''}>
                <td className="td-roster-name">{r.username}</td>
                <td>{r.class_group || '-'}</td>
                {tracks.map((tr) => {
                  const t = r.tracks[tr.track_key];
                  return (
                    <td key={tr.track_key} className="td-roster-track">
                      <div className="exam-progress-bar"><div className="exam-progress-fill" style={{ width: `${t.pct}%` }} /></div>
                      <span className="td-roster-track-n">{t.passed}/{t.total}</span>
                    </td>
                  );
                })}
                <td>
                  {r.weakest_topic
                    ? <span className="td-weak" style={{ background: masteryColor(r.weakest_topic.mastery), color: masteryText(r.weakest_topic.mastery) }}>{topicLabel(r.weakest_topic, i18n.language === 'ar')}</span>
                    : <span className="td-muted">-</span>}
                </td>
                <td>{r.passed_this_week}</td>
                <td>{fmtDate(r.last_active_at, i18n.language) || <span className="td-muted">{tx('roster.never', 'Never')}</span>}</td>
                <td>
                  {r.flag_types.map((f) => (
                    <span key={f} className={`td-flag-chip td-flag-chip-${f === 'falling_behind' ? 'amber' : 'red'}`}>{tx(`flags.type.${f}`, f.replace('_', ' '))}</span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LeaguePanel({ league, loading, tx }) {
  if (loading && !league) {
    return <div className="card animate-pulse" style={{ height: 180 }} />;
  }
  if (!league) return null;
  const top = league.top || [];
  const podium = top.slice(0, 3);
  const rest = top.slice(3, 10);
  const medals = ['gold', 'silver', 'bronze'];
  return (
    <div className="card td-league">
      {top.length === 0 ? (
        <div className="td-flags-empty">{tx('league.empty', 'No levels passed yet this week. The board resets every week, so anyone can top it.')}</div>
      ) : (
        <>
          <div className="td-podium">
            {podium.map((e, i) => (
              <div key={e.username} className={`td-medal td-medal-${medals[i]}`}>
                <span className="td-medal-rank">{i + 1}</span>
                <span className="td-medal-name">{e.username}</span>
                {e.class_group ? <span className="td-medal-class">{e.class_group}</span> : null}
                <span className="td-medal-n">{tx('league.levels', '{{n}} levels', { n: e.levels_passed })}</span>
              </div>
            ))}
          </div>
          {rest.length > 0 ? (
            <ol className="td-league-list" start={4}>
              {rest.map((e) => (
                <li key={e.username}>
                  <span className="td-league-name">{e.username}{e.class_group ? <span className="td-muted"> {e.class_group}</span> : null}</span>
                  <span className="td-league-n">{e.levels_passed}</span>
                </li>
              ))}
            </ol>
          ) : null}
        </>
      )}
      {(league.class_totals || []).length > 1 ? (
        <div className="td-class-totals">
          <div className="td-class-totals-title">{tx('league.class_totals', 'Class vs class')}</div>
          {league.class_totals.map((c) => (
            <div key={c.class_group || '_none'} className="td-class-total">
              <span>{c.class_group || tx('league.unassigned', 'No class')}</span>
              <span className="td-muted">{tx('league.students', '{{n}} students', { n: c.students })}</span>
              <span className="td-class-total-n">{c.levels_passed}</span>
            </div>
          ))}
        </div>
      ) : null}
      <div className="td-rules">
        {tx('league.rules', 'Ranked by levels passed since the week started. Ties broken by average time per level. Resets every week.')}
      </div>
    </div>
  );
}

// ── Mock exams (Chunk 51) ────────────────────────────────────────────────────
// Teachers create a mock from ONE choice (the verbal/quant split) plus a class
// and a window; the paper shape comes from the backend template (Qudurat:
// 4 x 24 x 25 min, passages not yet included). Scores shown are RAW
// percentages — never labelled as a Qiyas score.

function toLocalInput(d) {
  // Date -> value for <input type="datetime-local"> in the browser's zone.
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function fmtWhen(iso, locale) {
  try {
    return new Date(iso).toLocaleString(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function pctCell(v) {
  if (v == null) return <span className="td-muted">-</span>;
  return <span className="td-weak" style={{ background: masteryColor(v), color: masteryText(v) }}>{Math.round(v)}%</span>;
}

function MockResults({ assignmentId, tx, i18n, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    mocksApi.results(assignmentId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e?.error?.message || 'Could not load results.'); });
    return () => { cancelled = true; };
  }, [assignmentId]);

  if (error) return <div className="td-error" style={{ marginTop: 10 }}>{error}</div>;
  if (!data) return <div className="animate-pulse" style={{ height: 120, marginTop: 10 }} />;
  const sm = data.summary;
  const sections = data.assignment.sections;
  const exportResults = () => {
    const header = ['Student', 'Class', 'Status', 'Score', 'Total', '%', 'Verbal %', 'Quant %',
                    ...Array.from({ length: sections }, (_, i) => `Section ${i + 1} %`), 'Minutes', 'Submitted'];
    const rows = data.rows.map((r) => [
      r.username, r.class_group || '', r.status, r.score ?? '', r.total ?? '', r.pct ?? '',
      r.verbal_pct ?? '', r.quant_pct ?? '',
      ...(r.section_pcts || Array.from({ length: sections }, () => '')),
      r.elapsed_minutes ?? '', r.submitted_at ? r.submitted_at.slice(0, 16).replace('T', ' ') : '',
    ]);
    const text = '﻿' + [header, ...rows].map((c) => c.map(csvCell).join(',')).join('\r\n');
    downloadCsv(`mock_${assignmentId}_results.csv`, text);
  };
  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 10 }}>
        <strong>{tx('mocks.results_title', 'Results')}</strong>
        <span className="td-muted">{tx('mocks.summary', '{{submitted}} of {{eligible}} submitted', { submitted: sm.submitted, eligible: sm.eligible })}</span>
        {sm.mean_pct != null ? (
          <span className="td-muted">
            {tx('mocks.means', 'class mean {{pct}}% (verbal {{v}}%, quant {{q}}%)',
                { pct: sm.mean_pct, v: sm.mean_verbal_pct ?? '-', q: sm.mean_quant_pct ?? '-' })}
          </span>
        ) : null}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn btn-ghost btn-sm" onClick={exportResults} disabled={sm.submitted === 0}>
          {tx('mocks.export', 'Export CSV')}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{tx('mocks.close', 'Close')}</button>
      </div>
      <div className="td-grid-scroll">
        <table className="td-roster">
          <thead>
            <tr>
              <th>{tx('roster.student', 'Student')}</th>
              <th>{tx('roster.class', 'Class')}</th>
              <th>{tx('mocks.status', 'Status')}</th>
              <th>{tx('mocks.score', 'Score')}</th>
              <th>{tx('mocks.verbal', 'Verbal')}</th>
              <th>{tx('mocks.quant', 'Quant')}</th>
              {Array.from({ length: sections }, (_, i) => <th key={i}>S{i + 1}</th>)}
              <th>{tx('mocks.time', 'Time')}</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.user_id}>
                <td className="td-roster-name">{r.username}</td>
                <td>{r.class_group || '-'}</td>
                <td>
                  <span className={`td-flag-chip td-flag-chip-${r.status === 'submitted' ? 'green' : r.status === 'in_progress' ? 'amber' : 'red'}`}>
                    {tx(`mocks.status_${r.status}`, r.status.replace('_', ' '))}
                  </span>
                </td>
                <td>{r.pct == null ? <span className="td-muted">-</span> : <>{pctCell(r.pct)} <span className="td-muted">{r.score}/{r.total}</span></>}</td>
                <td>{pctCell(r.verbal_pct)}</td>
                <td>{pctCell(r.quant_pct)}</td>
                {Array.from({ length: sections }, (_, i) => <td key={i}>{pctCell(r.section_pcts ? r.section_pcts[i] : null)}</td>)}
                <td>{r.elapsed_minutes == null ? <span className="td-muted">-</span> : tx('mocks.minutes', '{{n}} min', { n: r.elapsed_minutes })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="td-rules">{data.meta.score_note}</div>
    </div>
  );
}

const MOCK_CSS = `
.td-mocks { padding: 0; overflow: hidden; }
.td-mocks-head { display: flex; flex-wrap: wrap; gap: 14px; align-items: center; padding: 18px 22px; border-bottom: 1px solid var(--border); }
.td-mocks-spec { display: flex; gap: 10px; flex-wrap: wrap; flex: 1; }
.td-mocks-spec .td-stat { min-width: 108px; }
.td-mocks-note { flex-basis: 100%; font-size: 0.78rem; color: var(--text-muted); }
.td-mocks-body { padding: 18px 22px; }
.td-mocks-sheet { background: var(--brand-sand, #F5F2EC); border: 1px solid var(--border); border-radius: 12px; padding: 18px 20px; margin-bottom: 18px; }
.td-mocks-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 14px 18px; }
.td-mocks-grid .form-group { margin: 0; }
.td-mocks-grid .form-input { background: #fff; }
.td-mocks-full { grid-column: 1 / -1; }
.td-mocks-split { display: flex; height: 14px; border-radius: 999px; overflow: hidden; background: var(--brand-grey, #D6DBE0); margin: 8px 0 10px; }
.td-mocks-split > span:first-child { background: var(--brand-green, #0B5D4B); transition: width 0.2s ease; }
.td-mocks-split > span:last-child { background: var(--brand-navy, #1C2733); flex: 1; }
.td-mocks-range { width: 100%; accent-color: var(--brand-green, #0B5D4B); }
.td-mocks-legend { display: flex; flex-wrap: wrap; gap: 8px 18px; align-items: center; font-size: 0.84rem; color: var(--text-secondary); }
.td-mocks-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-inline-end: 6px; vertical-align: middle; }
.td-mocks-presets { display: flex; gap: 8px; margin-inline-start: auto; }
.td-mocks-preset { font-size: 0.76rem; font-weight: 700; padding: 4px 10px; border-radius: 999px; border: 1px solid var(--border); background: #fff; cursor: pointer; color: var(--text-secondary); }
.td-mocks-preset.on { background: var(--brand-green, #0B5D4B); color: #fff; border-color: var(--brand-green, #0B5D4B); }
.td-mocks-actions { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
.td-mocks-list { display: flex; flex-direction: column; gap: 12px; }
.td-mocks-item { border: 1px solid var(--border); border-radius: 12px; padding: 14px 18px; border-inline-start: 5px solid var(--brand-grey, #D6DBE0); background: #fff; }
.td-mocks-item.open { border-inline-start-color: var(--brand-green, #0B5D4B); }
.td-mocks-item.scheduled { border-inline-start-color: var(--brand-gold, #C6A85B); }
.td-mocks-item.closed { border-inline-start-color: var(--brand-grey, #D6DBE0); }
.td-mocks-item-row { display: flex; flex-wrap: wrap; gap: 8px 14px; align-items: center; }
.td-mocks-item-title { font-weight: 800; font-size: 1.02rem; color: var(--text-primary); }
.td-mocks-meta { display: flex; flex-wrap: wrap; gap: 6px 16px; margin-top: 6px; font-size: 0.82rem; color: var(--text-muted); }
.td-mocks-meta b { color: var(--text-secondary); font-weight: 600; }
.td-mocks-counts { display: flex; gap: 14px; margin-inline-start: auto; }
.td-mocks-count { text-align: center; min-width: 54px; }
.td-mocks-count-n { display: block; font-weight: 800; font-size: 1.15rem; line-height: 1.1; color: var(--text-primary); }
.td-mocks-count-l { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.4px; color: var(--text-muted); }
.td-mocks-empty { text-align: center; padding: 36px 16px; color: var(--text-muted); }
.td-mocks-empty-icon { font-size: 2rem; margin-bottom: 6px; }
@media (max-width: 600px) { .td-mocks-head, .td-mocks-body { padding: 14px 16px; } .td-mocks-counts { margin-inline-start: 0; width: 100%; } }
`;

function MocksPanel({ orgId, isAdmin, classes, tx, i18n }) {
  const [list, setList] = useState(null);
  const [template, setTemplate] = useState(null);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openResults, setOpenResults] = useState(null);
  const [form, setForm] = useState(() => {
    const open = new Date(); open.setMinutes(0, 0, 0); open.setHours(open.getHours() + 1);
    const close = new Date(open.getTime() + 7 * 86400000);
    return { title: '', class_group: '', verbal_pct: 55, opens_at: toLocalInput(open), closes_at: toLocalInput(close) };
  });

  const load = useCallback(() => {
    mocksApi.listAssignments(isAdmin ? orgId : undefined)
      .then((d) => { setList(d.assignments); setTemplate(d.template); setError(null); })
      .catch((e) => setError(e?.error?.message || 'Could not load mock exams.'));
  }, [orgId, isAdmin]);
  useEffect(() => { if (orgId) load(); }, [orgId, load]);

  const create = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await mocksApi.createAssignment({
        ...(isAdmin ? { org_id: orgId } : {}),
        exam: 'qudurat',
        title: form.title.trim(),
        class_group: form.class_group || null,
        verbal_pct: Number(form.verbal_pct),
        opens_at: new Date(form.opens_at).toISOString(),
        closes_at: new Date(form.closes_at).toISOString(),
      });
      setShowForm(false);
      setForm((f) => ({ ...f, title: '' }));
      load();
    } catch (e) {
      const d = e?.error?.details;
      const short = d && typeof d === 'object'
        ? Object.entries(d).map(([k, v]) => `${k}: ${v.available}/${v.needed}`).join(', ')
        : '';
      setError((e?.error?.message || 'Could not create the mock exam.') + (short ? ` (${short})` : ''));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(tx('mocks.confirm_delete', 'Delete "{{title}}"? Only possible while no student has started it.', { title: a.title }))) return;
    try { await mocksApi.deleteAssignment(a.id); load(); }
    catch (e) { setError(e?.error?.message || 'Could not delete.'); }
  };

  const stateChip = (st) => {
    const tone = st === 'open' ? 'green' : st === 'scheduled' ? 'amber' : 'red';
    return <span className={`td-flag-chip td-flag-chip-${tone}`}>{tx(`mocks.state_${st}`, st)}</span>;
  };
  const vp = Number(form.verbal_pct);
  const total = template ? template.sections * template.questions_per_section : 96;
  const nVerbal = Math.round(total * vp / 100);
  const presets = [
    { v: 55, label: tx('mocks.preset_science', 'Science pathway') },
    { v: 75, label: tx('mocks.preset_theory', 'Sharia / business') },
  ];

  return (
    <div className="card td-mocks">
      <style>{MOCK_CSS}</style>

      <div className="td-mocks-head">
        <div className="td-mocks-spec">
          <div className="td-stat td-stat-navy"><span className="td-stat-n">{total}</span><span className="td-stat-l">{tx('mocks.questions', 'questions')}</span></div>
          <div className="td-stat td-stat-navy"><span className="td-stat-n">{template ? template.sections : 4}</span><span className="td-stat-l">{tx('mocks.sections', 'sections')}</span></div>
          <div className="td-stat td-stat-navy"><span className="td-stat-n">{template ? template.sections * template.minutes_per_section : 100}</span><span className="td-stat-l">{tx('mocks.minutes_l', 'minutes')}</span></div>
          <div className="td-stat td-stat-green"><span className="td-stat-n">1</span><span className="td-stat-l">{tx('mocks.attempt', 'attempt each')}</span></div>
        </div>
        <button type="button" className={`btn btn-sm ${showForm ? 'btn-ghost' : 'btn-primary'}`} onClick={() => setShowForm((v) => !v)}>
          {showForm ? tx('mocks.cancel', 'Cancel') : tx('mocks.new', '+ New mock exam')}
        </button>
        <div className="td-mocks-note">
          {tx('mocks.note', 'Qudurat format: {{q}} questions per section, {{m}} minutes each, sections locked in order. Reading-comprehension passages are not yet included.',
              { q: template ? template.questions_per_section : 24, m: template ? template.minutes_per_section : 25 })}
        </div>
      </div>

      <div className="td-mocks-body">
        {error ? <div className="td-error" style={{ marginBottom: 12 }}>{error}</div> : null}

        {showForm ? (
          <div className="td-mocks-sheet">
            <div className="td-mocks-grid">
              <div className="form-group">
                <label className="form-label">{tx('mocks.f_title', 'Title')}</label>
                <input className="form-input" maxLength={120} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                       placeholder={tx('mocks.f_title_ph', 'e.g. Mock 1 - October')} autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">{tx('mocks.f_class', 'Assign to')}</label>
                <select className="form-input" value={form.class_group} onChange={(e) => setForm({ ...form, class_group: e.target.value })}>
                  <option value="">{tx('all_classes', 'All classes')}</option>
                  {classes.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="form-group td-mocks-full">
                <div className="td-mocks-legend">
                  <label className="form-label" style={{ margin: 0 }}>{tx('mocks.f_split', 'Verbal / quantitative split')}</label>
                  <div className="td-mocks-presets">
                    {presets.map((p) => (
                      <button key={p.v} type="button" className={`td-mocks-preset${vp === p.v ? ' on' : ''}`}
                              onClick={() => setForm({ ...form, verbal_pct: p.v })}>
                        {p.label} {p.v}/{100 - p.v}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="td-mocks-split" role="img" aria-label={`${vp}% verbal, ${100 - vp}% quantitative`}>
                  <span style={{ width: `${vp}%` }} /><span />
                </div>
                <input type="range" className="td-mocks-range" min={10} max={90} step={5} value={vp}
                       onChange={(e) => setForm({ ...form, verbal_pct: e.target.value })} />
                <div className="td-mocks-legend">
                  <span><i className="td-mocks-dot" style={{ background: 'var(--brand-green, #0B5D4B)' }} /><b>{vp}%</b> {tx('mocks.verbal', 'Verbal')} · {nVerbal} {tx('mocks.questions', 'questions')}</span>
                  <span><i className="td-mocks-dot" style={{ background: 'var(--brand-navy, #1C2733)' }} /><b>{100 - vp}%</b> {tx('mocks.quant', 'Quantitative')} · {total - nVerbal} {tx('mocks.questions', 'questions')}</span>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">{tx('mocks.f_opens', 'Opens')}</label>
                <input type="datetime-local" className="form-input" value={form.opens_at} onChange={(e) => setForm({ ...form, opens_at: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{tx('mocks.f_closes', 'Closes')}</label>
                <input type="datetime-local" className="form-input" value={form.closes_at} onChange={(e) => setForm({ ...form, closes_at: e.target.value })} />
              </div>

              <div className="td-mocks-full td-mocks-actions">
                <button type="button" className="btn btn-primary" onClick={create} disabled={saving || !form.title.trim()}>
                  {saving ? '...' : tx('mocks.create', 'Create mock exam')}
                </button>
                <span className="td-muted" style={{ fontSize: '0.8rem' }}>
                  {tx('mocks.create_hint', 'Every student gets a different paper and one attempt. It appears on their dashboard while the window is open.')}
                </span>
              </div>
            </div>
          </div>
        ) : null}

        {list === null ? (
          <div className="animate-pulse" style={{ height: 80 }} />
        ) : list.length === 0 ? (
          <div className="td-mocks-empty">
            <div className="td-mocks-empty-icon">&#x1F4DD;</div>
            {tx('mocks.empty', 'No mock exams yet. Create one and assign it to a class.')}
          </div>
        ) : (
          <div className="td-mocks-list">
            {list.map((a) => (
              <div key={a.id} className={`td-mocks-item ${a.state}`}>
                <div className="td-mocks-item-row">
                  <span className="td-mocks-item-title">{a.title}</span>
                  {stateChip(a.state)}
                  <div className="td-mocks-counts">
                    <div className="td-mocks-count"><span className="td-mocks-count-n">{a.attempts_submitted}</span><span className="td-mocks-count-l">{tx('mocks.submitted', 'submitted')}</span></div>
                    <div className="td-mocks-count"><span className="td-mocks-count-n">{a.attempts_started - a.attempts_submitted}</span><span className="td-mocks-count-l">{tx('mocks.in_progress', 'in progress')}</span></div>
                  </div>
                  <button type="button" className={`btn btn-sm ${openResults === a.id ? 'btn-ghost' : 'btn-primary'}`} onClick={() => setOpenResults(openResults === a.id ? null : a.id)}>
                    {openResults === a.id ? tx('mocks.hide', 'Hide results') : tx('mocks.results', 'Results')}
                  </button>
                  {a.attempts_started === 0 ? (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => remove(a)}>{tx('mocks.delete', 'Delete')}</button>
                  ) : null}
                </div>
                <div className="td-mocks-meta">
                  <span>{tx('mocks.m_class', 'Class')}: <b>{a.class_group || tx('all_classes', 'All classes')}</b></span>
                  <span>{tx('mocks.m_split', 'Split')}: <b>{a.verbal_pct} / {a.quant_pct}</b></span>
                  <span>{tx('mocks.m_paper', 'Paper')}: <b>{a.total_questions} q · {a.total_minutes} min</b></span>
                  <span>{tx('mocks.m_window', 'Window')}: <b>{fmtWhen(a.opens_at, i18n.language)} &rarr; {fmtWhen(a.closes_at, i18n.language)}</b></span>
                </div>
                {openResults === a.id ? <MockResults assignmentId={a.id} tx={tx} i18n={i18n} onClose={() => setOpenResults(null)} /> : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function TeacherDashboard() {
  const { t, i18n } = useTranslation();
  const { user }    = useAuth();
  const params      = useParams();
  const isAdmin     = user?.role === 'drfahm_admin';
  const orgId       = params.orgId ? Number(params.orgId) : user?.org_id ?? null;

  const tx = useCallback(
    (key, defaultValue, opts = {}) => t(`teacher.${key}`, { defaultValue, ...opts }),
    [t]
  );

  const [exam, setExam]           = useState('qudurat');
  const [classGroup, setClassGroup] = useState('');
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [highlight, setHighlight] = useState(null);
  const [league, setLeague]       = useState(null);
  const [leagueLoading, setLeagueLoading] = useState(true);

  useEffect(() => {
    if (!orgId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    schoolsApi.dashboard(orgId, { exam, classGroup: classGroup || undefined })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e?.error?.message || 'Could not load the dashboard.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, exam, classGroup]);

  // D3: weekly school league. Leaders get their own org; admins viewing
  // /school/:orgId pass org_id explicitly (backend rejects it for non-admins).
  useEffect(() => {
    if (!orgId) { setLeagueLoading(false); return; }
    let cancelled = false;
    setLeagueLoading(true);
    examsApi.leaderboard(exam, { scope: 'school', period: 'week', orgId: isAdmin ? orgId : undefined })
      .then((d) => { if (!cancelled) setLeague(d); })
      .catch(() => { if (!cancelled) setLeague(null); })
      .finally(() => { if (!cancelled) setLeagueLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, exam, isAdmin]);

  const pick = (userId) => {
    setHighlight(userId);
    const el = document.getElementById(`td-student-${userId}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const classes = data?.meta?.classes || [];
  const isAr = i18n.language === 'ar';
  const brand = useMemo(() => resolveBranding(data?.meta), [data]);
  const palette = useMemo(() => (brand.hex ? { ...C, navy: brand.hex, green: brand.hex } : C), [brand.hex]);
  const rootStyle = useMemo(() => brandStyle(brand.hex), [brand.hex]);
  const examName = t(`common.${exam}`, { defaultValue: exam === 'qudurat' ? 'Qudurat' : 'Tahsili' });

  // PDF report = the browser's print-to-PDF with the print stylesheet in
  // global.css (A4 landscape, nav/controls hidden, report header shown).
  const downloadPdf = () => { window.print(); };

  const exportCsv = () => {
    if (!data) return;
    const slug = data.meta.org?.slug || `org-${orgId}`;
    const cls  = data.meta.class_group ? data.meta.class_group.replace(/[^\w-]+/g, '_') : 'all';
    const day  = data.meta.generated_at ? data.meta.generated_at.slice(0, 10) : '';
    downloadCsv(`${slug}_${exam}_${cls}_${day}.csv`, buildRosterCsv(data, tx, isAr));
  };

  return (
    <PaletteContext.Provider value={palette}>
      <Navbar />
      <div className={`page${brand.hex ? ' td-branded' : ''}`} style={rootStyle}>
        <div className="page-header td-header">
          <div className="td-brand">
            {brand.logo ? <img className="td-brand-logo" src={brand.logo} alt={brand.name || ''} /> : null}
            <div>
              <h1 className="page-title">
                {brand.name || tx('title', 'Teacher dashboard')}
              </h1>
              <p className="page-subtitle">
                {tx('subtitle', 'What your students have done, who needs attention, and what to teach next.')}
              </p>
            </div>
          </div>
          <div className="td-controls">
            <button type="button" className="btn btn-primary btn-sm" onClick={downloadPdf} disabled={!data}>
              {tx('pdf_report', 'Download PDF report')}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={exportCsv}
                    disabled={!data || data.roster.length === 0}>
              {tx('export_csv', 'Export CSV')}
            </button>
            {isAdmin ? <Link to="/admin" className="btn btn-ghost btn-sm">{tx('back_admin', 'Admin')}</Link> : null}
          </div>
        </div>

        {/* Print-only report header */}
        {data ? (
          <div className="td-print-header">
            <div className="td-print-header-row">
              {brand.logo ? <img className="td-print-logo" src={brand.logo} alt="" /> : null}
              <div className="td-print-title">{brand.name || data.meta.org.name}</div>
            </div>
            <div className="td-print-meta">
              {examName}
              {' \u00b7 '}
              {classGroup || tx('all_classes', 'All classes')}
              {' \u00b7 '}
              {tx('print.generated', 'Report generated {{when}}', { when: fmtDate(data.meta.generated_at, i18n.language) })}
              {' \u00b7 '}
              {tx('print.by', 'DrFahm teacher dashboard')}
            </div>
          </div>
        ) : null}

        {/* Excel-style class tabs */}
        {classes.length > 0 ? (
          <div className="td-classtabs" role="tablist">
            <button type="button" role="tab" aria-selected={classGroup === ''}
                    className={`td-classtab${classGroup === '' ? ' td-classtab-active' : ''}`}
                    onClick={() => setClassGroup('')}>
              {tx('all_classes', 'All classes')}
            </button>
            {classes.map((c) => (
              <button key={c} type="button" role="tab" aria-selected={classGroup === c}
                      className={`td-classtab${classGroup === c ? ' td-classtab-active' : ''}`}
                      onClick={() => setClassGroup(c)}>
                {c}
              </button>
            ))}
          </div>
        ) : null}

        {!orgId ? (
          <div className="card empty-state">
            <div className="empty-state-text">{tx('no_org', 'This account is not linked to a school.')}</div>
          </div>
        ) : error ? (
          <div className="card td-error">{error}</div>
        ) : loading && !data ? (
          <>
            <div className="card animate-pulse" style={{ height: 200, marginBottom: 24 }} />
            <div className="card animate-pulse" style={{ height: 320, marginBottom: 24 }} />
            <div className="card animate-pulse" style={{ height: 320 }} />
          </>
        ) : data ? (
          <div className={loading ? 'td-refreshing' : ''}>
            <div className="section">
              <div className="td-section-head">
                <p className="section-title">
                  {tx('overview.title_exam', '{{exam}} class overview', { exam: examName })}
                  {classGroup ? <span className="td-section-class">{classGroup}</span> : null}
                </p>
                <div className="td-tabs" role="tablist">
                  {EXAMS.map((ex) => (
                    <button key={ex} type="button" role="tab" aria-selected={exam === ex}
                            className={`td-tab${exam === ex ? ' td-tab-active' : ''}`}
                            onClick={() => setExam(ex)}>
                      {t(`common.${ex}`, { defaultValue: ex === 'qudurat' ? 'Qudurat' : 'Tahsili' })}
                    </button>
                  ))}
                </div>
              </div>
              <ContextStrip data={data} tx={tx} i18n={i18n} />
              <OverviewPanel data={data} tx={tx} examName={examName} isAr={isAr} />
            </div>

            <div className="section">
              <p className="section-title">{tx('flags.title', 'Needs attention')}</p>
              <FlagsPanel data={data} tx={tx} onPick={pick} />
            </div>

            <div className="section">
              <p className="section-title">{tx('radar.title', 'Class topic profile')}</p>
              <RadarPanel data={data} tx={tx} i18n={i18n} />
            </div>

            <div className="section">
              <p className="section-title">{tx('grid.title', 'What to teach next')}</p>
              <GridPanel data={data} tx={tx} highlight={highlight} isAr={isAr} />
            </div>

            <div className="section">
              <p className="section-title">{tx('roster.title', 'Students')}</p>
              <RosterPanel data={data} tx={tx} i18n={i18n} highlight={highlight} />
            </div>

            <div className="section">
              <p className="section-title">{tx('league.title', 'School league this week')}</p>
              <LeaguePanel league={league} loading={leagueLoading} tx={tx} />
            </div>

            {/* Chunk 51: mock exams — teacher-built from one setting, one attempt per student */}
            <div className="section" id="td-mocks">
              <p className="section-title">{tx('mocks.title', 'Mock exams')}</p>
              <MocksPanel orgId={orgId} isAdmin={isAdmin} classes={classes} tx={tx} i18n={i18n} />
            </div>

            <p className="td-footnote">
              {tx('footnote', 'Week starts {{day}}. Data as of {{when}}.', {
                day: data.thresholds.week_start_day === 6
                  ? tx('sunday', 'Sunday')
                  : tx('monday', 'Monday'),
                when: fmtDate(data.meta.generated_at, i18n.language),
              })}
            </p>
          </div>
        ) : null}
      </div>
    </PaletteContext.Provider>
  );
}